import { fromByteArray as b64FromByteArray } from 'base64-js';
import apiClient from '../middleware/apiClient';
import {
  APPLE_PAY,
} from '../helpers/apis';

import { getApplePaymentRequest } from '../helpers/applePay';


const { location } = window;

export const SET_APPLE_PAY = 'SET_APPLE_PAY';
export const SET_APPLE_PAY_SUCCESS = 'SET_APPLE_PAY_SUCCESS';
export const SET_APPLE_PAY_FAILURE = 'SET_APPLE_PAY_FAILURE';

function dispatchSetApplePay() {
  return {
    type: SET_APPLE_PAY,
    payload: window.ApplePaySession.canMakePaymentsWithActiveCard('merchant.com.comcast.cim.myaccount'),
  };
}

function dispatchSetApplePaySuccess(payload) {
  return {
    type: SET_APPLE_PAY_SUCCESS,
    payload,
  };
}

function dispatchSetApplePayFailure() {
  return {
    type: SET_APPLE_PAY_FAILURE,
  };
}

export const setApplePay = () => async (dispatch) => {
  if (!window.ApplePaySession) {
    return dispatch(dispatchSetApplePayFailure());
  }

  return dispatch(dispatchSetApplePay()).payload
    .then((canMakePaymentsWithActiveCard) => {
      if (canMakePaymentsWithActiveCard) {
        return dispatch(dispatchSetApplePaySuccess());
      }
      return dispatch(dispatchSetApplePayFailure());
    });
};

export const BEGIN_APPLE_PAY = 'BEGIN_APPLE_PAY';
export const GET_APPLE_PAY_SESSION = 'GET_APPLE_PAY_SESSION';
export const GET_APPLE_PAY_SESSION_SUCCESS = 'GET_APPLE_PAY_SESSION_SUCCESS';
export const GET_APPLE_PAY_SESSION_FAILURE = 'GET_APPLE_PAY_SESSION_FAILURE';
export const APPLE_PAY_SUCCESS = 'APPLE_PAY_SUCCESS';
export const CANCEL_APPLEPAY_SESSION = 'CANCEL_APPLEPAY_SESSION';

function dispatchBeginApplePay() {
  return {
    type: BEGIN_APPLE_PAY,
  };
}

function dispatchGetApplePaySession(data) {
  return {
    type: GET_APPLE_PAY_SESSION,
    data,
    payload: apiClient.fetch(APPLE_PAY, { method: 'POST', body: JSON.stringify(data) }),
  };
}

function dispatchGetApplePaySessionSuccess() {
  return {
    type: GET_APPLE_PAY_SESSION_SUCCESS,
  };
}

function dispatchGetApplePaySessionFailure(error) {
  return {
    type: GET_APPLE_PAY_SESSION_FAILURE,
    payload: error,
  };
}

function dispatchApplePaySuccess(payload) {
  return {
    type: APPLE_PAY_SUCCESS,
    payload,
  };
}

function dispatchCancelApplePaySession() {
  return {
    type: CANCEL_APPLEPAY_SESSION,
  };
}

/**
 * @param {string} amount - The amount due today.
 * @param {Object} [applePayData] -
 *  Optional object passed for recurring payments if feature flag is on.
 * @param {'pending' | 'final'} applePayData.type -
 * This should be `pending` for autopay and `final` for others.
 * @param {string} applePayData.billingAmount - The amount for the recurring payment.
 * @param {string} [applePayData.recurringPaymentStartDate] -
 *  The date that represents when the automatic payment will begin.
 */
export const getApplePaySession = (amount, applePayData) => (dispatch) => {
  // if applePayData is passed then the feature flag is ON
  const applePayVersion = applePayData ? 14 : 2;
  const paymentRequest = getApplePaymentRequest(amount, applePayData);

  // Autopay case (feature flag OFF)
  if (!amount) {
    paymentRequest.total.type = 'pending';
  }

  return new Promise((resolve, reject) => {
    // Create apple pay session with paymentRequest data object
    // version 14 is available in macOS 13 and iOS 16
    // https://developer.apple.com/documentation/applepayontheweb/apple-pay-on-the-web-version-14-release-notes
    const applePaySession = new window.ApplePaySession(applePayVersion, paymentRequest);

    applePaySession.onpaymentauthorized = (event) => {
      const applePayPayload = JSON.stringify(event.payment.token.paymentData);
      const applePayBytes = new TextEncoder().encode(applePayPayload);
      const applePayBase64result = b64FromByteArray(applePayBytes);

      const payload = {
        type: 'ApplePay',
        amount: amount || 1.00,
        data: applePayBase64result,
      };
      // Happy path exit point
      resolve({ payload, applePaySession });
      return dispatch(dispatchApplePaySuccess(payload));
    };


    applePaySession.oncancel = (event) => {
      dispatch(dispatchCancelApplePaySession());
      reject(event);
    };

    applePaySession.onvalidatemerchant = event => dispatch(dispatchGetApplePaySession({
      validationURL: event.validationURL,
      merchantIdentifier: 'merchant.com.comcast.cim.myaccount',
      domainName: location.host,
      displayName: 'Comcast',
    })).payload
      .then((merchantSession) => {
        // session only valid for 5 minutes
        // pass merchantSession object/identifier/string to apple's completeMV function
        // (handshake)
        applePaySession.completeMerchantValidation(merchantSession);
        // User is now presented to confirm/authorize payment on their device iPhone or watch.
        return dispatch(dispatchGetApplePaySessionSuccess());
      })
      .catch((error) => {
        reject(error);
        applePaySession.abort();
        return dispatch(dispatchGetApplePaySessionFailure(error));
      });
    applePaySession.begin();

    return dispatch(dispatchBeginApplePay());
  });
};

export default getApplePaymentRequest;
